import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import FadeInFromLeft from '../components/Welcome/FadeInFromLeft';
import firebaseAuth from '../services/firebaseAuth';

const colors = {
  mainBlue: "#061941",
  xanthous: "#F2AF29",
  isabelline: "#F4F3EE",
  carrieban: "#3C6E71",
  cafeNoir: "#583E23",
};

export default function CreateUser() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'student',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [refCode, setRefCode] = useState(null);
const [googleLoading, setGoogleLoading] = useState(false);
  const location = useLocation();
  const [selectedRole, setSelectedRole] = useState('student');

  // Get role from URL params
  useEffect(() => {
    const role = searchParams.get('role');
    if (role && ['student', 'professor'].includes(role)) {
      setSelectedRole(role);
      setFormData(prev => ({ ...prev, role }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
      const ref = params.get("ref");

      if(ref) {
        setRefCode(ref);
      }
  }, [location]);

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    try {
      const result = await firebaseAuth.signInWithGoogle(selectedRole, refCode);
      const { user, token, isNewUser } = result;
      
      // Store user data in localStorage
      localStorage.setItem('user', JSON.stringify({ ...user, token }));
      localStorage.setItem('token', token);
      
      if (isNewUser) {
        // New user with pre-selected role, redirect to appropriate dashboard
        if (user.role === 'student') {
          navigate('/student-welcome');
        } else if (user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/professor-welcome');
        }
      } else {
        // Existing user, redirect to appropriate dashboard
        if (user.role === 'student') {
          navigate('/student-welcome');
        } else if (user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/professor-welcome');
        }
      }
    } catch (error) {
      console.error('Google sign up error:', error);
      alert('❌ Google sign up failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    return emailRegex.test(email) && email.includes('.') && email.split('.').pop().length >= 2;
  };

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 6) {
      errors.push('Password must be at least 6 characters long');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[a-zA-Z]/.test(password)) {
      errors.push('Password must contain at least one letter');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    if (!acceptTerms) {
      alert('❌ Please accept the Terms and Conditions and Privacy Policy to continue.');
      return;
    }
    
    // Validate email format
    if (!isValidEmail(formData.email)) {
      alert('❌ Please enter a valid email address.');
      return;
    }
    
    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      alert(`❌ Password requirements:\n• ${passwordValidation.errors.join('\n• ')}`);
      return;
    }
    
    // Validate professor email
    if (formData.role === 'professor' && !formData.email.toLowerCase().endsWith('.edu')) {
      alert('❌ Professors must use a .edu email address.');
      return;
    }
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {...formData, ref: refCode });
      
      if (response.data.requiresVerification) {
        // Redirect to email verification with email pre-filled
        navigate('/email-verification', { 
          state: { 
            email: formData.email,
            message: response.data.message 
          } 
        });
      } else {
        // Fallback to login if verification not required
        navigate('/login');
      }
    } catch (err) {
      if (err.response?.data?.message) {
        alert(`❌ ${err.response.data.message}`);
      } else {
        alert('❌ Registration failed.');
      }
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        minWidth: "100vw",
        background: `linear-gradient(120deg, ${colors.mainBlue} 70%, ${colors.xanthous} 92%, ${colors.xanthous} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Segoe UI, Arial, sans-serif"
      }}
    >
      <FadeInFromLeft delay={200}>
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            background: colors.isabelline,
            borderRadius: "16px",
            boxShadow: `0 4px 24px 0 ${colors.cafeNoir}22`,
            padding: "2.25rem 2rem 2rem 2rem",
            margin: "2rem",
            display: "flex",
            flexDirection: "column",
          }}
        >
        <h2
          style={{
            fontSize: "2rem",
            fontWeight: 700,
            fontFamily: "Poppins, Segoe UI, Arial, sans-serif",
            textAlign: "center",
            color: colors.mainBlue,
            marginBottom: "0.5rem",
            letterSpacing: "1px"
          }}
        >
          Create {selectedRole === 'professor' ? 'Professor' : 'Student'} Account
        </h2>
        
        {selectedRole === 'professor' && (
          <div style={{
            textAlign: "center",
            marginBottom: "1.5rem",
            padding: "0.75rem",
            background: `${colors.xanthous}20`,
            borderRadius: "8px",
            border: `1px solid ${colors.xanthous}40`
          }}>
            <p style={{
              margin: 0,
              fontSize: "0.9rem",
              color: colors.carrieban,
              fontWeight: 500
            }}>
              ⚠️ Professor accounts require a .edu email address
            </p>
          </div>
        )}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

          {/* Name */}
          <div>
            <label style={{ fontWeight: 600, color: colors.carrieban, marginBottom: ".15rem" }}>
              Name <span style={{ color: "#b91c1c" }}>*</span>
            </label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "0.9rem",
                border: `1.7px solid ${colors.carrieban}99`,
                borderRadius: "10px",
                fontFamily: "inherit",
                fontSize: "1.05rem",
                marginTop: '.5rem'
              }}
            />
          </div>

          {/* Email */}
          <div>
            <label style={{ fontWeight: 600, color: colors.carrieban, marginBottom: ".15rem" }}>
              Email <span style={{ color: "#b91c1c" }}>*</span>
              {formData.role === 'professor' && (
                <span style={{ fontSize: "0.9rem", color: colors.carrieban, fontWeight: "normal" }}>
                  {" "}(.edu required for professors)
                </span>
              )}
            </label>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "0.9rem",
                border: `1.7px solid ${colors.carrieban}99`,
                borderRadius: "10px",
                fontFamily: "inherit",
                fontSize: "1.05rem",
                marginTop: '.5rem'
              }}
            />
          </div>

          {/* Password */}
          <div>
            <label style={{ fontWeight: 600, color: colors.carrieban, marginBottom: ".15rem" }}>
              Password <span style={{ color: "#b91c1c" }}>*</span>
            </label>
            <input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "0.9rem",
                border: `1.7px solid ${colors.carrieban}99`,
                borderRadius: "10px",
                fontFamily: "inherit",
                fontSize: "1.05rem",
                marginTop: '.5rem'
              }}
            />
            {formData.password && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                <div style={{ color: colors.carrieban, fontWeight: 600, marginBottom: '0.25rem' }}>
                  Password Requirements:
                </div>
                <div style={{ color: formData.password.length >= 6 ? '#10b981' : '#ef4444' }}>
                  ✓ At least 6 characters
                </div>
                <div style={{ color: /\d/.test(formData.password) ? '#10b981' : '#ef4444' }}>
                  ✓ At least one number
                </div>
                <div style={{ color: /[a-zA-Z]/.test(formData.password) ? '#10b981' : '#ef4444' }}>
                  ✓ At least one letter
                </div>
              </div>
            )}
          </div>

          {/* Terms and Conditions Checkbox */}
          <div style={{ marginTop: "1rem" }}>
            <label style={{ 
              display: "flex", 
              alignItems: "flex-start", 
              gap: "0.75rem", 
              cursor: "pointer",
              fontSize: "0.95rem",
              lineHeight: 1.4
            }}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                required
                style={{
                  marginTop: "0.1rem",
                  transform: "scale(1.1)",
                  accentColor: colors.xanthous
                }}
              />
              <span style={{ color: colors.carrieban }}>
                I agree to the{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: colors.xanthous, 
                    textDecoration: "none",
                    fontWeight: 600
                  }}
                  onMouseOver={(e) => e.target.style.textDecoration = "underline"}
                  onMouseOut={(e) => e.target.style.textDecoration = "none"}
                >
                  Terms and Conditions
                </a>
                {' '}and{' '}
                <a 
                  href="/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: colors.xanthous, 
                    textDecoration: "none",
                    fontWeight: 600
                  }}
                  onMouseOver={(e) => e.target.style.textDecoration = "underline"}
                  onMouseOut={(e) => e.target.style.textDecoration = "none"}
                >
                  Privacy Policy
                </a>
                <span style={{ color: "#b91c1c" }}> *</span>
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            style={{
              width: "100%",
              marginTop: "1.2rem",
              background: colors.xanthous,
              color: colors.mainBlue,
              padding: "1rem 0",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "1.15rem",
              border: "none",
              cursor: "pointer",
              boxShadow: `0 2px 8px 0 ${colors.xanthous}33`,
              transition: "background 0.12s, color 0.12s"
            }}
          >
            Register
          </button>

          {/* Divider */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            margin: '0.75rem 0',
            color: colors.carrieban
          }}>
            <div style={{ flex: 1, height: '1px', background: colors.carrieban + '40' }}></div>
            <span style={{ margin: '0 1rem', fontSize: '0.9rem', fontWeight: 500 }}>or</span>
            <div style={{ flex: 1, height: '1px', background: colors.carrieban + '40' }}></div>
          </div>

          {/* Google Sign Up Button */}
          <button
            type="button"
            onClick={handleGoogleSignUp}
            disabled={googleLoading}
            style={{
              width: "100%",
              background: "#fff",
              color: colors.mainBlue,
              padding: "1rem 0",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "1rem",
              border: `2px solid ${colors.carrieban}40`,
              cursor: googleLoading ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              boxShadow: "0 2px 8px 0 rgba(0,0,0,0.1)"
            }}
            onMouseOver={(e) => {
              if (!googleLoading) {
                e.target.style.border = `2px solid ${colors.xanthous}`;
                e.target.style.background = colors.xanthous + "10";
              }
            }}
            onMouseOut={(e) => {
              if (!googleLoading) {
                e.target.style.border = `2px solid ${colors.carrieban}40`;
                e.target.style.background = "#fff";
              }
            }}
          >
            {googleLoading ? (
              <>
                <div style={{
                  width: "20px",
                  height: "20px",
                  border: `2px solid ${colors.carrieban}40`,
                  borderTop: `2px solid ${colors.xanthous}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}></div>
                Signing up...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Register with Google
              </>
            )}
          </button>

        </form>
        </div>
      </FadeInFromLeft>
      
      {/* CSS for spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}